import { useCallback, useEffect, useRef, useState } from "react";
import { waitForElement } from "../../../utils/tour-steps-utils/tour-utils";
import type { TourStep } from "../../../data/constants/tour-steps/add_item_steps";

interface UseTourProps {
  steps: TourStep[];
  enabled: boolean;
}

interface UseTourReturn {
  currentStep: number;
  targetRect: DOMRect | null;
  isWaiting: boolean;
  isActive: boolean;
  tourCompleted: boolean;
  exitTour: () => void;
}

const ANIMATION_SETTLE_MS = 1000;

export const useTour = ({ steps, enabled }: UseTourProps): UseTourReturn => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  const currentStepRef = useRef(-1);
  currentStepRef.current = currentStep;

  const isActive = currentStep >= 0;

  // Start/stop tour based on enabled prop
  useEffect(() => {
    if (enabled) {
      setCurrentStep(0);
      setTourCompleted(false);
    } else {
      setCurrentStep(-1);
      setTargetRect(null);
      setIsWaiting(false);
    }
  }, [enabled]);

  // Track the target element's position for spotlight + tooltip
  useEffect(() => {
    if (currentStep < 0 || currentStep >= steps.length) return;

    const step = steps[currentStep];
    const highlightSelector = step.highlightElement || step.element;
    let rafId: number;
    let resizeObserver: ResizeObserver | null = null;
    let cancelled = false;

    const startTracking = () => {
      if (cancelled) return;

      const el = document.querySelector(highlightSelector);
      if (!el) return;

      const updateRect = () => {
        if (cancelled) return;
        setTargetRect(el.getBoundingClientRect());
      };

      updateRect();
      setIsWaiting(false);

      // RAF loop while CSS transitions / cascading animations settle
      const start = Date.now();
      const trackAnimations = () => {
        if (cancelled || Date.now() - start >= ANIMATION_SETTLE_MS) return;
        updateRect();
        rafId = requestAnimationFrame(trackAnimations);
      };
      rafId = requestAnimationFrame(trackAnimations);

      resizeObserver = new ResizeObserver(updateRect);
      resizeObserver.observe(el);
    };

    if (step.waitFor) {
      setIsWaiting(true);
      setTargetRect(null);
      waitForElement(step.element, startTracking);
    } else {
      startTracking();
    }

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [currentStep, steps]);

  // Click-to-advance via capture phase (fires before target's own handler)
  useEffect(() => {
    if (currentStep < 0 || isWaiting) return;

    const step = steps[currentStep];
    if (!step?.element) return;

    const handleClick = (e: MouseEvent) => {
      const contentEl = document.querySelector(step.element);
      if (!contentEl?.contains(e.target as HTMLElement)) return;

      if (currentStepRef.current >= steps.length - 1) {
        setTourCompleted(true);
        setCurrentStep(-1);
        setTargetRect(null);
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [currentStep, isWaiting, steps]);

  // Recalculate rect on window resize
  useEffect(() => {
    if (currentStep < 0) return;

    const step = steps[currentStep];
    if (!step) return;
    const selector = step.highlightElement || step.element;

    const handleResize = () => {
      const el = document.querySelector(selector);
      if (el) setTargetRect(el.getBoundingClientRect());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentStep, steps]);

  const exitTour = useCallback(() => {
    setCurrentStep(-1);
    setTargetRect(null);
  }, []);

  return { currentStep, targetRect, isWaiting, isActive, tourCompleted, exitTour };
};
